'use client'

import { ShoppingBasket, Trash2 } from 'lucide-react'
import { Button } from '../ui/button'
import { useCartStore } from '@/lib/context/cartContext'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { queryHotelById } from '@/lib/payload/queries'
import { Hotel } from '@/payload-types'
import { Language } from '@/types/language.types'
import { ImageBlock } from '../common/ImageBlock'
import { format } from 'date-fns'
import { CartCountdown } from '../common/CartCountdown'
import { useLocale, useTranslations } from 'next-intl'
import { AddonPreview } from '@/lib/booking-engine/types'
import { RateOfferBadges } from '../common/RateOfferBadges'

const CartSheet = () => {
  const { cart, clearCart, resetCart } = useCartStore()

  const t = useTranslations()
  const locale = useLocale() as Language

  const pathname = usePathname()

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hotel, setHotel] = useState<Hotel | null>(null)

  const isHidden = pathname.includes('/checkout') || pathname.includes('/confirmation')
  const cartHotelId = cart?.hotel?.payloadcmsId

  useEffect(() => {
    if (pathname.includes('/confirmation')) {
      resetCart()
    }
  }, [pathname, resetCart])

  useEffect(() => {
    setHotel(null)
  }, [cartHotelId])

  useEffect(() => {
    if (isHidden && open) {
      setOpen(false)
    }
  }, [isHidden, open])

  useEffect(() => {
    if (!open || !cartHotelId || hotel !== null) return

    let ignore = false
    setLoading(true)

    queryHotelById({ id: String(cartHotelId), locale })
      .then((payloadHotel) => {
        if (!ignore) {
          setHotel(payloadHotel)
        }
      })
      .catch((error) => {
        console.error('[CartSheet] hotel fetch failed:', error)
        if (!ignore) {
          setHotel(null)
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false)
        }
      })

    return () => {
      ignore = true
    }
  }, [open, cartHotelId, locale, hotel])

  if (isHidden || !cart) return null

  const addonLines: AddonPreview[] = cart.items.flatMap((item) =>
    (item.addons ?? []).map((a) => ({
      id: a.id,
      itemId: item.id,
      mappingId: a.mappingId,
      name: a.name,
      quantity: a.quantity,
      totalPrice: a.totalPrice,
      currency: a.currency,
    })),
  )

  const offerTitles = new Map(
    (hotel?.offers?.docs ?? [])
      .filter((doc) => typeof doc !== 'number')
      .map((doc) => [doc.id, doc.title]),
  )

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size='icon'
          variant={'ghost'}
          className='relative flex size-10 items-center justify-center'
          aria-label={
            cart && cart.items.length > 0
              ? `${t('your_cart')} (${cart.items.length})`
              : t('your_cart')
          }
        >
          <ShoppingBasket strokeWidth={1.5} className='size-7' />

          {cart && cart.items.length > 0 && (
            <span className='text-foreground absolute -top-2 -right-2 flex size-5 items-center justify-center rounded-full bg-red-300 text-sm'>
              {cart.items.length}
            </span>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent className='flex w-full flex-col sm:w-[600px]'>
        <SheetHeader>
          <SheetTitle>{t('your_cart')}</SheetTitle>
          <button
            onClick={() => clearCart()}
            className='text-muted-foreground hover:text-destructive flex w-fit items-center gap-1.5 text-xs transition-colors'
          >
            <Trash2 className='size-3.5' />
            {t('clear_cart')}
          </button>
        </SheetHeader>

        {cart.expiresAt && <CartCountdown expiresAt={cart.expiresAt} />}

        <div className='flex-1 space-y-4 overflow-y-auto pb-32'>
          {cart?.items.map((item, i) => {
            const room = hotel?.rooms?.find((r) => r.id === item.payloadRoomId)
            const firstImage = room?.images?.[0]?.image
            const isHotelLoading = loading && !hotel

            return (
              <div key={item.id} className='space-y-2 border-b pb-4 last:border-b-0'>
                <p>
                  {t('room')} {i + 1}
                </p>

                <div className='flex gap-3'>
                  <div className='w-24 shrink-0 overflow-hidden rounded-lg'>
                    {isHotelLoading ? (
                      <div className='aspect-square w-full animate-pulse bg-stone-200' />
                    ) : (
                      <ImageBlock imageBlock={firstImage} imgClassName='object-cover' fill />
                    )}
                  </div>
                  <div className='flex min-w-0 flex-1 flex-col justify-between'>
                    <div className='flex items-start justify-between gap-2'>
                      <div className='min-w-0'>
                        {isHotelLoading ? (
                          <div className='mb-2 h-4 w-32 animate-pulse rounded bg-stone-200' />
                        ) : (
                          room && <p className='leading-tight font-bold'>{room.roomType}</p>
                        )}
                        <div className='flex flex-wrap items-center gap-1.5 pt-0.5'>
                          {item.payloadcmsOfferId && offerTitles.get(item.payloadcmsOfferId) ? (
                            <p className='text-xs font-medium'>
                              {offerTitles.get(item.payloadcmsOfferId)}
                            </p>
                          ) : (
                            item.rateName?.[locale] && (
                              <p className='text-muted-foreground text-xs'>
                                {item.rateName[locale]}
                              </p>
                            )
                          )}
                          <RateOfferBadges item={item} />
                          {item.isRefundable != null && (
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                item.isRefundable
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-stone-100 text-stone-600'
                              }`}
                            >
                              {item.isRefundable ? t('free_cancellation') : t('non_refundable')}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className='shrink-0 text-right'>
                        {item.originalTotalPrice != null &&
                          item.originalTotalPrice > (item.totalPrice ?? 0) && (
                            <p className='text-muted-foreground text-xs line-through'>
                              {item.originalTotalPrice} {item.currency}
                            </p>
                          )}
                        <p className='font-medium'>
                          {item.totalPrice} {item.currency}
                        </p>
                      </div>
                    </div>

                    <div className='flex flex-col space-y-1'>
                      {isHotelLoading ? (
                        <div className='h-3 w-28 animate-pulse rounded bg-stone-200' />
                      ) : (
                        <p className='text-sm'>{hotel?.title}</p>
                      )}

                      <div className='flex items-center justify-between'>
                        <p className='text-muted-foreground text-sm'>
                          {format(item.checkIn, 'dd MMM yyyy')} -{' '}
                          {format(item.checkOut, 'dd MMM yyyy')}
                        </p>
                        <p className='text-sm'>
                          {item.adults} {t('adults')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}

          {addonLines.length > 0 && (
            <div>
              <p className='text-lg font-medium'>{t('addons_extras')}</p>
              <div className='flex w-full flex-col space-y-4'>
                {cart.items.map((item, i) => {
                  const room = hotel?.rooms?.find((r) => r.id === item.payloadRoomId)
                  const itemAddons = addonLines.filter((a) => a.itemId === item.id)
                  if (itemAddons.length === 0) return null

                  return (
                    <div key={item.id} className='w-full'>
                      {cart.items.length > 1 && (
                        <p>
                          {room?.roomType} ({t('room')}: {i + 1})
                        </p>
                      )}

                      <div className='flex w-full flex-col'>
                        {itemAddons.map((addon) => (
                          <div key={addon.id} className='flex items-center justify-between'>
                            <span className='text-muted-foreground'>
                              {addon.name?.[locale] ?? addon.name?.en ?? ''}
                            </span>

                            <div className='flex items-center space-x-2'>
                              <span className='text-muted-foreground text-sm'>
                                {addon.quantity}
                              </span>
                              <span className='text-muted-foreground text-sm'>x</span>
                              <span>
                                {addon.totalPrice.toLocaleString('nb-NO')} {addon.currency}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <SheetFooter className='absolute bottom-0 left-0 flex w-full !flex-col border-t border-gray-200 bg-white p-4'>
          <div className='flex w-full justify-between pb-2 text-lg font-semibold'>
            <span>{t('total')}</span>
            <span>
              {cart.totalPrice} {cart.currency}
            </span>
          </div>
          <SheetClose asChild className='m-0'>
            <Button asChild className='w-full'>
              <Link href='/checkout'>{t('view_cart_checkout')}</Link>
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default CartSheet
